import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { FileText, Receipt, Wallet, TrendingUp, ChevronDown, MessageCircle, AlertTriangle, X, GripVertical, BarChart3, Table as TableIcon, ShieldAlert, Clock, Coins } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Line, ComposedChart } from "recharts";
import { AiResult } from "../components/AiResultView";
import { usePolling } from "../lib/usePolling";
import { PINNABLE_REPORTS } from "../lib/reportPins";

const COLORS = ["#C84B31", "#E2A349", "#4E6E58", "#4A5568", "#915A1A", "#2D523B"];

function Collapsible({ title, right, children, testId, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-surface mb-8" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-4 text-left" data-testid={`${testId}-toggle`}>
        <div className="section-title mb-0">{title}</div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block">{right}</div>
          <ChevronDown className={`w-4 h-4 text-[#68665E] transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && <div className="px-4 sm:px-6 pb-6">{right && <div className="sm:hidden mb-3">{right}</div>}{children}</div>}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, testId, onClick }) {
  return (
    <div
      className={`card-surface p-6 ${onClick ? "cursor-pointer transition-colors hover:border-[#C84B31] hover:bg-[#FDFBF7]" : ""}`}
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#68665E] font-semibold">{label}</div>
          <div className="font-headings text-3xl font-bold text-[#1A1A18] mt-3">{value}</div>
          {sub && <div className="text-xs text-[#68665E] mt-2">{sub}</div>}
          {onClick && <div className="text-[11px] text-[#C84B31] mt-2 font-medium">View all →</div>}
        </div>
        <div className="w-10 h-10 bg-[#FDFCF8] border border-[#E5E3DB] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#C84B31]" />
        </div>
      </div>
    </div>
  );
}

function ReportPinCard({ pinKey, onUnpin }) {
  const cfg = PINNABLE_REPORTS[pinKey];
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cfg) return;
    api.get(cfg.endpoint).then((r) => setData(r.data)).catch(() => setData({}));
  }, [pinKey]); // eslint-disable-line
  if (!cfg) return null;
  const rows = cfg.getRows(data).slice(0, 8);
  return (
    <div className="card-surface p-4" data-testid={`report-pin-${pinKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="section-title mb-0">{cfg.title}</div>
        <button onClick={() => onUnpin(pinKey)} className="btn-ghost text-[#8A2526]" title="Unpin from dashboard" data-testid={`report-unpin-${pinKey}`}><X className="w-4 h-4" /></button>
      </div>
      {!data ? <div className="text-sm text-[#68665E] py-4">Loading…</div> :
        rows.length === 0 ? <div className="text-sm text-[#68665E] py-4">No data for this report yet.</div> :
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>{cfg.columns.map((c) => <th key={c.key} className={`table-th ${c.money ? "text-right" : ""}`}>{c.label}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={cfg.rowKey(r) || i} className="table-row">
                  {cfg.columns.map((c) => {
                    const v = r[c.key];
                    const cls = c.profit ? (Number(v) >= 0 ? "text-[#2F6F4E]" : "text-[#8A2526]") : "";
                    return <td key={c.key} className={`table-td ${c.money ? "text-right" : ""} ${c.mono ? "font-mono text-xs" : ""} ${cls}`}>{c.money ? formatINR(v) : v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [profit, setProfit] = useState(null);
  const [pendingLH, setPendingLH] = useState(null);
  const [pins, setPins] = useState([]);
  const [reportPins, setReportPins] = useState([]);
  const [ewb, setEwb] = useState(null);
  const [recentFilter, setRecentFilter] = useState("");
  const [stuckLrs, setStuckLrs] = useState(null);
  const [lhPayables, setLhPayables] = useState(null);
  const [pnl, setPnl] = useState(null);

  const loadDash = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/charts").then((r) => setCharts(r.data)).catch(() => {});
    api.get("/reports/lr-profit?limit=10").then((r) => setProfit(r.data)).catch(() => {});
    api.get("/reports/pending-lh-balances").then((r) => setPendingLH(r.data)).catch(() => {});
    api.get("/ai/pins").then((r) => setPins(r.data)).catch(() => {});
    api.get("/dashboard/report-pins").then((r) => setReportPins(r.data)).catch(() => {});
    api.get("/dashboard/ewb-expiry").then((r) => setEwb(r.data)).catch(() => {});
    api.get("/dashboard/stuck-lrs").then((r) => setStuckLrs(r.data)).catch(() => {});
    api.get("/dashboard/lh-payables").then((r) => setLhPayables(r.data)).catch(() => {});
    api.get("/dashboard/pnl-monthly").then((r) => setPnl(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadDash(); }, [loadDash]);
  usePolling(loadDash, 30000);

  async function unpin(id) {
    try { await api.delete(`/ai/pins/${id}`); setPins((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Could not unpin this report"); }
  }

  async function unpinReport(key) {
    try {
      await api.delete(`/dashboard/report-pins/${key}`);
      setReportPins((p) => p.filter((x) => x.report_key !== key));
      toast.info("Removed from dashboard");
    } catch { toast.error("Could not unpin this report"); }
  }

  const dragIndex = useRef(null);
  function onPinDrop(i) {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === i) return;
    const prev = pins;
    const next = pins.slice();
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setPins(next);
    api.post("/ai/pins/reorder", { ids: next.map((p) => p.id) })
      .catch(() => { setPins(prev); toast.error("Could not save the new order"); });
  }
  function setPinView(id, view) {
    const prev = pins;
    setPins((ps) => ps.map((p) => (p.id === id ? { ...p, default_view: view } : p)));
    api.put(`/ai/pins/${id}`, { default_view: view })
      .catch(() => { setPins(prev); toast.error("Could not save the default view"); });
  }

  if (!stats) {
    return <div className="text-[#68665E] text-sm" data-testid="dashboard-loading">Loading dashboard...</div>;
  }

  const pieData = (stats.expense_by_category || []).map((e) => ({ name: e.category, value: e.total }));
  const monthly = (charts?.monthly || []).map((m) => ({ ...m, label: m.month.slice(2) }));
  const funnel = charts?.invoice_funnel || {};
  const pnlData = (pnl?.months || []).map((m) => ({ ...m, label: m.month.slice(2) }));

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Today</div>
        <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Hi, {user?.name?.split(" ")[0] || "there"}.</h1>
        <p className="text-sm text-[#68665E] mt-2">Here&apos;s how the yard looks this month.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Total LRs" value={stats.total_lrs} sub={`${stats.in_transit} in transit · ${stats.delivered} delivered`} icon={FileText} testId="stat-total-lrs" onClick={() => navigate("/lrs")} />
        <StatCard label="Pending Invoices" value={stats.pending_invoices} sub={formatINR(stats.pending_amount) + " outstanding"} icon={Receipt} testId="stat-pending-invoices" onClick={() => navigate("/invoices?status=pending")} />
        <StatCard label="Monthly Revenue" value={formatINR(stats.monthly_revenue)} sub="Paid invoices this month" icon={TrendingUp} testId="stat-monthly-revenue" onClick={() => navigate("/invoices?status=paid")} />
        <StatCard label="Monthly Expenses" value={formatINR(stats.monthly_expenses)} sub="Across all categories" icon={Wallet} testId="stat-monthly-expenses" onClick={() => navigate("/expenses")} />
      </div>

      {ewb && ewb.count > 0 && (
        <Collapsible
          title={<span className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#8A2526]" /> E-Way Bills Expiring / Expired</span>}
          testId="dashboard-ewb-expiry"
          right={<div className="text-sm text-[#68665E]">{ewb.expired} expired · {ewb.count - ewb.expired} expiring soon</div>}
        >
          <p className="text-sm text-[#68665E] mb-3">E-way bills valid for ≤48 hours (or already expired). Extend the EWB and upload the updated copy to the LR to clear these.</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-th">LR #</th><th className="table-th">EWB No.</th><th className="table-th">Valid Until</th><th className="table-th">Status</th></tr></thead>
              <tbody>
                {ewb.rows.map((r) => (
                  <tr key={r.file_id} className="table-row" data-testid={`ewb-expiry-row-${r.file_id}`}>
                    <td className="table-td font-mono text-xs">{r.lr_id ? <button className="text-[#C84B31] hover:underline" onClick={()=>navigate(`/lr/${r.lr_id}`)}>{r.lr_number || "LR"}</button> : (r.lr_number || "—")}</td>
                    <td className="table-td font-mono text-xs">{r.ewb_number || "—"}</td>
                    <td className="table-td">{formatDate(r.valid_until)}</td>
                    <td className="table-td">
                      {r.status === "expired"
                        ? <span className="badge-error" data-testid={`ewb-status-${r.file_id}`}>Expired {Math.abs(r.days_left)}d ago</span>
                        : <span className="badge-pending" data-testid={`ewb-status-${r.file_id}`}>{r.days_left <= 0 ? "Today" : `${r.days_left}d left`}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}

      {charts && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard label="Avg Days to Pay" value={charts.avg_days_to_pay} sub={`${charts.paid_invoice_count} paid invoices`} icon={TrendingUp} testId="stat-avg-days-pay" />
            <StatCard label="Avg Transit Time" value={`${charts.avg_transit_days} days`} sub="Pickup → delivery" icon={FileText} testId="stat-avg-transit" />
            <StatCard label="Invoice Pipeline" value={`${funnel.paid || 0} paid`} sub={`${funnel.pending || 0} pending · ${funnel.submitted || 0} submitted · ${funnel.overdue || 0} overdue`} icon={Receipt} testId="stat-invoice-funnel" onClick={() => navigate("/invoices")} />
          </div>

          <Collapsible title="Revenue vs Expenses — Last 12 Months" testId="dashboard-trend-chart">
            <div style={{ width: "100%", height: 300, minHeight: 300 }}>
              <ResponsiveContainer>
                <ComposedChart data={monthly}>
                  <CartesianGrid stroke="#EEEAE0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#68665E" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#68665E" }} width={48} />
                  <Tooltip formatter={(v, n) => n === "lrs" ? v : formatINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#4E6E58" maxBarSize={28} />
                  <Bar dataKey="expenses" name="Expenses" fill="#C84B31" maxBarSize={28} />
                  <Line dataKey="lrs" name="LRs" stroke="#E2A349" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Collapsible>
        </>
      )}

      {profit && profit.rows.length > 0 && (
        <Collapsible
          title="Profit per LR — Top 10"
          testId="dashboard-profit-table"
          right={<div className="text-sm text-[#68665E]">Net total: <span className={`font-semibold ${profit.totals.profit >= 0 ? "text-[#2F6F4E]" : "text-[#8A2526]"}`}>{formatINR(profit.totals.profit)}</span></div>}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-th">LR #</th><th className="table-th">Route</th><th className="table-th text-right">Freight</th><th className="table-th text-right">Expenses</th><th className="table-th text-right">Profit</th><th className="table-th text-right">Margin</th></tr></thead>
              <tbody>
                {profit.rows.map((r) => (
                  <tr key={r.lr_id} className="table-row" data-testid={`profit-row-${r.lr_id}`}>
                    <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={()=>navigate(`/lr/${r.lr_id}`)}>{r.lr_number}</button></td>
                    <td className="table-td text-sm">{r.route}</td>
                    <td className="table-td text-right">{formatINR(r.freight)}</td>
                    <td className="table-td text-right text-[#8A2526]">{formatINR(r.expense_allocated)}</td>
                    <td className={`table-td text-right font-semibold ${r.profit >= 0 ? "text-[#2F6F4E]" : "text-[#8A2526]"}`}>{formatINR(r.profit)}</td>
                    <td className="table-td text-right text-sm">{r.margin_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}

      {pendingLH && pendingLH.rows.length > 0 && (
        <Collapsible
          title={<span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#A0522D]" /> Pending Lorry-Hire Balances</span>}
          testId="dashboard-pending-lh"
          right={<div className="text-sm text-[#68665E]">{pendingLH.count} payable · <span className="font-semibold text-[#A0522D]">{formatINR(pendingLH.total)}</span></div>}
        >
          <p className="text-sm text-[#68665E] mb-3">Delivered consignments where we still owe the broker their lorry-hire balance. Clear these payments to close them out.</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-th">LR #</th><th className="table-th">Route</th><th className="table-th">Vehicle</th><th className="table-th">Broker</th><th className="table-th text-right">We Owe (Balance)</th><th className="table-th text-right">Action</th></tr></thead>
              <tbody>
                {pendingLH.rows.map((r, i) => (
                  <tr key={`${r.lr_id}-${i}`} className="table-row" data-testid={`pending-lh-row-${i}`}>
                    <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={()=>navigate(`/lr/${r.lr_id}`)}>{r.lr_number}</button></td>
                    <td className="table-td text-sm">{r.route}</td>
                    <td className="table-td font-mono text-xs">{r.vehicle_number || "—"}</td>
                    <td className="table-td text-sm">{r.broker_name || "—"}</td>
                    <td className="table-td text-right font-semibold text-[#A0522D]">{formatINR(r.lh_balance)}</td>
                    <td className="table-td text-right">
                      <button
                        className="btn-ghost text-[#1f8a4c]"
                        title="Message broker about the payment we owe"
                        data-testid={`pending-lh-whatsapp-${i}`}
                        onClick={() => {
                          const msg = `Regarding LR ${r.lr_number} (${r.route}), Vehicle ${r.vehicle_number} — pending lorry-hire balance payable to you: ${formatINR(r.lh_balance)}. We are processing your payment. Please share your account details / confirm receipt.`;
                          const num = (r.driver_phone || "").replace(/[^0-9]/g, "");
                          const base = num ? `https://wa.me/${num.length === 10 ? "91" + num : num}` : "https://wa.me/";
                          window.open(`${base}?text=${encodeURIComponent(msg)}`, "_blank");
                        }}
                      ><MessageCircle className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}

      {stuckLrs && stuckLrs.count > 0 && (
        <Collapsible
          title={<span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#A0522D]" /> Stuck LRs</span>}
          testId="dashboard-stuck-lrs"
          right={<div className="text-sm text-[#68665E]">{stuckLrs.count} sitting ≥ {stuckLrs.threshold_days}d in a stage</div>}
        >
          <p className="text-sm text-[#68665E] mb-3">Active consignments that haven&apos;t moved to their next stage for a while. Chase PODs, invoicing or delivery to clear them.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {stuckLrs.by_stage.map((s) => (
              <span key={s.stage} className="text-xs px-2 py-0.5 border border-[#E5E3DB] text-[#68665E]" data-testid={`stuck-stage-${s.stage}`}>{s.stage}: {s.count}</span>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-th">LR #</th><th className="table-th">Route</th><th className="table-th">Current Stage</th><th className="table-th">Waiting On</th><th className="table-th text-right">Days</th></tr></thead>
              <tbody>
                {stuckLrs.rows.map((r) => (
                  <tr key={r.lr_id} className="table-row" data-testid={`stuck-lr-row-${r.lr_id}`}>
                    <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={()=>navigate(`/lr/${r.lr_id}`)}>{r.lr_number}</button></td>
                    <td className="table-td text-sm">{r.route}</td>
                    <td className="table-td text-sm">{r.stage}</td>
                    <td className="table-td text-sm text-[#68665E]">{r.pending}</td>
                    <td className={`table-td text-right font-semibold ${r.severity === "late" ? "text-[#8A2526]" : "text-[#A0522D]"}`}>{r.days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}

      {lhPayables && lhPayables.count > 0 && (
        <Collapsible
          title={<span className="flex items-center gap-2"><Coins className="w-4 h-4 text-[#A0522D]" /> Lorry-Hire Payable</span>}
          testId="dashboard-lh-payables"
          right={<div className="text-sm text-[#68665E]">{lhPayables.count} vehicles · <span className="font-semibold text-[#A0522D]">{formatINR(lhPayables.total)}</span> due</div>}
        >
          <p className="text-sm text-[#68665E] mb-3">Total lorry hire you still owe brokers — unpaid advances plus unpaid balances across active LRs. Advance due {formatINR(lhPayables.advance_due_total)} · Balance due {formatINR(lhPayables.balance_due_total)}.</p>
          {lhPayables.by_broker.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {lhPayables.by_broker.slice(0, 8).map((b) => (
                <span key={b.broker} className="text-xs px-2 py-0.5 border border-[#E5E3DB] text-[#68665E]" data-testid={`lh-broker-chip`}>{b.broker}: {formatINR(b.due)}</span>
              ))}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="table-th">LR #</th><th className="table-th">Vehicle</th><th className="table-th">Broker</th><th className="table-th text-right">Advance Due</th><th className="table-th text-right">Balance Due</th><th className="table-th text-right">Total Due</th></tr></thead>
              <tbody>
                {lhPayables.rows.map((r, i) => (
                  <tr key={`${r.lr_id}-${i}`} className="table-row" data-testid={`lh-payable-row-${i}`}>
                    <td className="table-td"><button className="text-[#C84B31] hover:underline font-mono text-xs" onClick={()=>navigate(`/lr/${r.lr_id}`)}>{r.lr_number}</button></td>
                    <td className="table-td font-mono text-xs">{r.vehicle_number || "—"}</td>
                    <td className="table-td text-sm">{r.broker_name || "—"}</td>
                    <td className="table-td text-right text-sm">{r.advance_due ? formatINR(r.advance_due) : "—"}</td>
                    <td className="table-td text-right text-sm">{r.balance_due ? formatINR(r.balance_due) : "—"}</td>
                    <td className="table-td text-right font-semibold text-[#A0522D]">{formatINR(r.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Collapsible>
      )}

      {pnl && (
        <Collapsible
          title={<span className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#4E6E58]" /> Monthly P&amp;L (invoice-driven)</span>}
          testId="dashboard-pnl-monthly"
          right={<div className="text-sm text-[#68665E]">Net (12mo): <span className={`font-semibold ${pnl.total_net >= 0 ? "text-[#2F6F4E]" : "text-[#8A2526]"}`}>{formatINR(pnl.total_net)}</span></div>}
        >
          <p className="text-sm text-[#68665E] mb-3">Revenue from invoiced LRs vs expenses (module expenses + paid lorry hire), with net profit each month.</p>
          <div style={{ width: "100%", height: 300, minHeight: 300 }}>
            <ResponsiveContainer>
              <ComposedChart data={pnlData}>
                <CartesianGrid stroke="#EEEAE0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#68665E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#68665E" }} width={48} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#4E6E58" maxBarSize={28} />
                <Bar dataKey="expenses" name="Expenses" fill="#C84B31" maxBarSize={28} />
                <Line dataKey="net" name="Net" stroke="#E2A349" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Collapsible>
      )}

      {pins.length > 0 && (
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-pinned-reports">
          {pins.map((p, i) => (
            <div key={p.id} className="card-surface p-4" data-testid={`pinned-report-${p.id}`}
              draggable onDragStart={() => { dragIndex.current = i; }}
              onDragOver={(e) => e.preventDefault()} onDrop={() => onPinDrop(i)}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="cursor-grab active:cursor-grabbing text-[#A09E96] mt-0.5" title="Drag to reorder" data-testid={`pin-drag-${p.id}`}><GripVertical className="w-4 h-4" /></span>
                  <div className="min-w-0">
                    <div className="section-title mb-0 truncate">{p.title}</div>
                    {p.answer && <p className="text-xs text-[#68665E] mt-1">{p.answer}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.chart && (
                    <div className="inline-flex border border-[#E5E3DB]" title="Default view for this report">
                      <button onClick={() => setPinView(p.id, "chart")} className={`px-1.5 py-1 ${p.default_view === "chart" ? "bg-[#1A1A18] text-white" : "text-[#68665E]"}`} data-testid={`pin-view-chart-${p.id}`}><BarChart3 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setPinView(p.id, "table")} className={`px-1.5 py-1 ${p.default_view === "table" ? "bg-[#1A1A18] text-white" : "text-[#68665E]"}`} data-testid={`pin-view-table-${p.id}`}><TableIcon className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <button onClick={() => unpin(p.id)} className="btn-ghost text-[#8A2526]" title="Unpin from dashboard" data-testid={`unpin-${p.id}`}><X className="w-4 h-4" /></button>
                </div>
              </div>
              {p.columns?.length > 0
                ? <AiResult key={`${p.id}-${p.default_view}`} columns={p.columns} rows={p.rows} chart={p.chart} rowCount={p.row_count} defaultView={p.default_view} />
                : <div className="text-sm text-[#68665E] py-4">No data for this report right now.</div>}
            </div>
          ))}
        </div>
      )}

      {reportPins.length > 0 && (
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-report-pins">
          {reportPins.map((p) => (
            <ReportPinCard key={p.report_key} pinKey={p.report_key} onUnpin={unpinReport} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface">
          <div className="px-4 sm:px-6 py-4 border-b border-[#E5E3DB] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="section-title mb-0">Recent Lorry Receipts</div>
            <input className="input-field w-full sm:w-56 text-sm" placeholder="Filter by LR, route, party..." value={recentFilter} onChange={(e)=>setRecentFilter(e.target.value)} data-testid="dashboard-recent-filter" />
          </div>
          <div className="table-wrap border-0">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-th">LR #</th>
                  <th className="table-th">Date</th>
                  <th className="table-th">From → To</th>
                  <th className="table-th">Consignee</th>
                  <th className="table-th">Freight</th>
                  <th className="table-th">Status</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const q = recentFilter.trim().toLowerCase();
                  const rows = (stats.recent_lrs || []).filter((lr) => !q || [lr.lr_number, lr.from_location, lr.to_location, lr.consignee_name, lr.consignor_name].some((v) => String(v || "").toLowerCase().includes(q)));
                  return rows.length === 0 ? (
                    <tr><td className="table-td text-[#68665E]" colSpan={6}>{q ? "No matching LRs." : "No LRs yet. Create your first one."}</td></tr>
                  ) : rows.map((lr) => (
                  <tr key={lr.id} className="table-row">
                    <td className="table-td font-mono text-xs"><button className="text-[#C84B31] hover:underline" onClick={()=>navigate(`/lr/${lr.id}`)}>{lr.lr_number}</button></td>
                    <td className="table-td">{formatDate(lr.lr_date)}</td>
                    <td className="table-td">{lr.from_location} → {lr.to_location}</td>
                    <td className="table-td">{lr.consignee_name}</td>
                    <td className="table-td">{formatINR(lr.freight_amount)}</td>
                    <td className="table-td">
                      <span className={lr.status === "delivered" ? "badge-success" : lr.status === "cancelled" ? "badge-error" : "badge-pending"}>
                        {lr.status.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                )); })()}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card-surface p-6">
          <div className="section-title">Expense Breakdown — This Month</div>
          {pieData.length === 0 ? (
            <div className="text-sm text-[#68665E] py-8">No expenses recorded this month.</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50}>
                    {pieData.map((entry, i) => (<Cell key={entry.name || i} fill={COLORS[i % COLORS.length]} />))}
                  </Pie>
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
